import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { formatApiError, formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function NewInvoice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [salespeople, setSalespeople] = useState([]);
  const [shares, setShares] = useState({ referral_share: 0.2, closure_share: 0.8 });
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    invoice_date: new Date().toISOString().slice(0, 10),
    invoice_no: "",
    customer_name: "",
    project: "",
    invoice_value: "",
    purchase_value: "",
    customer_ref_by_id: "",
    deal_closed_by_id: "",
  });
  const [previewNo, setPreviewNo] = useState("");

  useEffect(() => {
    api.get("/salespeople").then((r) => {
      setSalespeople(r.data);
      if (user?.role === "salesperson") {
        setForm((f) => ({ ...f, customer_ref_by_id: user.id, deal_closed_by_id: user.id }));
      }
    });
    api.get("/settings").then((r) => setShares(r.data)).catch(() => {});
  }, [user]);

  const isDataEntry = user?.role === "data_entry";

  // Auto-fetch next invoice number based on date
  useEffect(() => {
    if (!form.invoice_date) return;
    let cancel = false;
    api.get(`/invoices/next-number?invoice_date=${form.invoice_date}`).then((r) => {
      if (!cancel) setPreviewNo(r.data.invoice_no);
    }).catch(() => {});
    return () => { cancel = true; };
  }, [form.invoice_date]);

  const margin = (parseFloat(form.invoice_value) || 0) - (parseFloat(form.purchase_value) || 0);
  const sameUser = form.customer_ref_by_id && form.customer_ref_by_id === form.deal_closed_by_id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_ref_by_id || !form.deal_closed_by_id) {
      toast.error("Both Customer Ref By and Deal Closed By are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        invoice_no: "", // let backend auto-assign to avoid gaps
        invoice_value: parseFloat(form.invoice_value),
        purchase_value: parseFloat(form.purchase_value),
      };
      await api.post("/invoices", payload);
      toast.success("Invoice submitted for approval");
      navigate("/invoices");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell title="New Invoice" subtitle="All fields are mandatory. Invoice number is auto-assigned in sequence.">
      <form onSubmit={submit} className="max-w-3xl">
        <Card className="p-6 border-zinc-200 shadow-none rounded-md bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="font-semibold">Invoice Date</Label>
              <Input type="date" required value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="invoice-date" />
            </div>
            <div>
              <Label className="font-semibold">Invoice No (auto-assigned)</Label>
              <Input value={previewNo} readOnly className="bg-zinc-50 font-mono tabular-nums" placeholder="CCC/001/26-27" data-testid="invoice-no" />
            </div>
            <div className="md:col-span-2">
              <Label className="font-semibold">Customer Name</Label>
              <Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="customer-name" />
            </div>
            <div className="md:col-span-2">
              <Label className="font-semibold">Project / Product</Label>
              <Input required value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} placeholder="e.g. ERP Rollout · Phase 2" data-testid="project" />
            </div>
            <div>
              <Label className="font-semibold">Invoice Value (Pre-GST) ₹</Label>
              <Input type="number" min="0" step="0.01" required value={form.invoice_value} onChange={(e) => setForm({ ...form, invoice_value: e.target.value })} data-testid="invoice-value" />
            </div>
            <div>
              <Label className="font-semibold">Purchase Value ₹</Label>
              <Input type="number" min="0" step="0.01" required value={form.purchase_value} onChange={(e) => setForm({ ...form, purchase_value: e.target.value })} data-testid="purchase-value" />
            </div>
            <div className="md:col-span-2 p-4 border border-zinc-200 rounded-md bg-zinc-50 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{isDataEntry ? "Margin (visible to admin only)" : "Computed Margin"}</div>
              <div className="text-2xl font-extrabold tracking-tighter font-heading tabular-nums" data-testid="computed-margin">
                {isDataEntry ? "—" : formatINR(margin)}
              </div>
            </div>
            <div>
              <Label className="font-semibold">Customer Ref By</Label>
              <Select value={form.customer_ref_by_id} onValueChange={(v) => setForm({ ...form, customer_ref_by_id: v })}>
                <SelectTrigger data-testid="ref-by-select"><SelectValue placeholder="Select salesperson" /></SelectTrigger>
                <SelectContent>
                  {salespeople.map((sp) => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-semibold">Deal Closed By</Label>
              <Select value={form.deal_closed_by_id} onValueChange={(v) => setForm({ ...form, deal_closed_by_id: v })}>
                <SelectTrigger data-testid="closed-by-select"><SelectValue placeholder="Select salesperson" /></SelectTrigger>
                <SelectContent>
                  {salespeople.map((sp) => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2 p-4 border border-zinc-200 rounded-md bg-white">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Attribution preview</div>
              {isDataEntry ? (
                <div className="text-sm text-zinc-500">Attribution is not visible for the data-entry role.</div>
              ) : sameUser ? (
                <div className="text-sm">100% of invoice value & margin goes to the same salesperson.</div>
              ) : form.customer_ref_by_id && form.deal_closed_by_id ? (
                <div className="text-sm">
                  <span className="font-semibold">{Math.round(shares.closure_share * 100)}%</span> attributed to deal closer ·{" "}
                  <span className="font-semibold">{Math.round(shares.referral_share * 100)}%</span> to referrer.
                </div>
              ) : (
                <div className="text-sm text-zinc-500">Select both fields to see attribution.</div>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="submit-invoice-button">
              {submitting ? "Submitting…" : "Submit for Approval"}
            </Button>
          </div>
        </Card>
      </form>
    </Shell>
  );
}
